import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save, Loader, CheckCircle, AlertTriangle, Building2, Leaf, Target,
  Scale, Users, BarChart3, ChevronRight, ChevronDown, Info, FileText,
  TrendingUp, Factory, Truck, Plane, Trash2, Zap, Package, DollarSign,
  Crown, RefreshCw, Upload, File, X, FileSpreadsheet, ArrowRight
} from 'lucide-react';
import { PremiumGate } from '../components/PremiumGate';
import { apiFetch } from '../services/api_service';


const API = process.env.REACT_APP_BACKEND_URL;

// Scope 3 Categories with descriptions
const SCOPE3_CATEGORIES = [
  { id: 'cat1_purchased_goods', name: 'Cat 1: Purchased Goods & Services', icon: Package, description: 'Extraction, production, and transportation of purchased goods' },
  { id: 'cat2_capital_goods', name: 'Cat 2: Capital Goods', icon: Building2, description: 'Production of capital goods purchased' },
  { id: 'cat3_fuel_energy', name: 'Cat 3: Fuel & Energy', icon: Zap, description: 'Fuel and energy-related activities not in Scope 1/2' },
  { id: 'cat4_upstream_transport', name: 'Cat 4: Upstream Transportation', icon: Truck, description: 'Transportation of purchased goods' },
  { id: 'cat5_waste', name: 'Cat 5: Waste Generated', icon: Trash2, description: 'Disposal and treatment of waste' },
  { id: 'cat6_business_travel', name: 'Cat 6: Business Travel', icon: Plane, description: 'Employee business travel' },
  { id: 'cat7_employee_commuting', name: 'Cat 7: Employee Commuting', icon: Users, description: 'Employee commuting to work' },
  { id: 'cat8_upstream_leased', name: 'Cat 8: Upstream Leased Assets', icon: Building2, description: 'Operation of leased assets' },
  { id: 'cat9_downstream_transport', name: 'Cat 9: Downstream Transportation', icon: Truck, description: 'Transportation of sold products' },
  { id: 'cat10_processing', name: 'Cat 10: Processing of Sold Products', icon: Factory, description: 'Processing of intermediate products' },
  { id: 'cat11_use_of_products', name: 'Cat 11: Use of Sold Products', icon: Package, description: 'End use of sold products' },
  { id: 'cat12_end_of_life', name: 'Cat 12: End-of-Life Treatment', icon: Trash2, description: 'Disposal of sold products' },
  { id: 'cat13_downstream_leased', name: 'Cat 13: Downstream Leased Assets', icon: Building2, description: 'Operation of assets leased to others' },
  { id: 'cat14_franchises', name: 'Cat 14: Franchises', icon: Building2, description: 'Operation of franchises' },
  { id: 'cat15_investments', name: 'Cat 15: Investments', icon: TrendingUp, description: 'Investments in other companies' },
];

// IRO Topics for assessment
const IRO_TOPICS = [
  { code: 'E1', name: 'Climate Change', category: 'Environmental' },
  { code: 'E2', name: 'Pollution', category: 'Environmental' },
  { code: 'E3', name: 'Water & Marine Resources', category: 'Environmental' },
  { code: 'E4', name: 'Biodiversity & Ecosystems', category: 'Environmental' },
  { code: 'E5', name: 'Resource Use & Circular Economy', category: 'Environmental' },
  { code: 'S1', name: 'Own Workforce', category: 'Social' },
  { code: 'S2', name: 'Workers in Value Chain', category: 'Social' },
  { code: 'S3', name: 'Affected Communities', category: 'Social' },
  { code: 'S4', name: 'Consumers & End-users', category: 'Social' },
  { code: 'G1', name: 'Business Conduct', category: 'Governance' },
];

// Section Component
const DataSection = ({ title, icon: Icon, color, isOpen, onToggle, children, badge }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
    <button
      onClick={onToggle}
      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <span className="font-semibold text-slate-900">{title}</span>
        {badge && (
          <span className="px-2 py-0.5 text-xs font-bold bg-emerald-100 text-emerald-700 rounded-full">
            {badge}
          </span>
        )}
      </div>
      {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
    </button>
    {isOpen && <div className="px-6 pb-6 border-t border-slate-100 pt-4">{children}</div>}
  </div>
);

// Input Field Component
const InputField = ({ label, value, onChange, type = 'number', placeholder, suffix, help }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-slate-700">{label}</label>
    <div className="relative">
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>
      )}
    </div>
    {help && <p className="text-xs text-slate-500">{help}</p>}
  </div>
);

// Main Component
const ESGDataEntry = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('taxonomy');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  
  // File Upload State
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  
  // EU Taxonomy State
  const [taxonomyData, setTaxonomyData] = useState({
    total_revenue: 0,
    eligible_revenue: 0,
    aligned_revenue: 0,
    total_capex: 0,
    green_capex: 0,
    total_opex: 0,
    green_opex: 0,
  });
  
  // Scope 3 State
  const [scope3Data, setScope3Data] = useState({
    scope1: 0,
    scope2_location: 0,
    scope2_market: 0,
    categories: {},
  });
  
  // IRO State
  const [iroData, setIroData] = useState({
    topics: IRO_TOPICS.map(t => ({ ...t, impact: 0, financial: 0 })),
    materiality_threshold: 3.0,
  });
  
  // SEC Climate State
  const [secData, setSecData] = useState({
    total_revenue: 0,
    total_assets: 0,
    materiality_threshold_pct: 1.0,
    physical_acute: 0,
    physical_chronic: 0,
    transition_policy: 0,
    transition_technology: 0,
    transition_market: 0,
    transition_reputation: 0,
  });
  
  // Load existing data
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const headers = { 'Authorization': `Bearer ${token}` };
      
      try {
        const [taxonomyRes, scope3Res, iroRes, secRes] = await Promise.all([
          fetch(`${API}/api/esg-data/eu-taxonomy`, { headers }),
          fetch(`${API}/api/esg-data/scope3-emissions`, { headers }),
          fetch(`${API}/api/esg-data/iro-assessment`, { headers }),
          fetch(`${API}/api/esg-data/sec-climate`, { headers }),
        ]);
        
        if (taxonomyRes) {
          const data = await taxonomyRes.json();
          if (data.data) setTaxonomyData(prev => ({ ...prev, ...data.data }));
        }
        
        if (scope3Res) {
          const data = await scope3Res.json();
          if (data.data) {
            setScope3Data(prev => ({
              ...prev,
              scope1: data.data.scope1 || 0,
              scope2_location: data.data.scope2_location || 0,
              scope2_market: data.data.scope2_market || 0,
              categories: data.data.categories || {},
            }));
          }
        }
        
        if (iroRes) {
          const data = await iroRes.json();
          if (data.data?.topics) {
            setIroData(prev => ({
              ...prev,
              topics: data.data.topics,
              materiality_threshold: data.data.materiality_threshold || 3.0,
            }));
          }
        }
        
        if (secRes) {
          const data = await secRes.json();
          if (data.data) {
            setSecData(prev => ({
              ...prev,
              total_revenue: data.data.total_revenue || 0,
              total_assets: data.data.total_assets || 0,
              materiality_threshold_pct: data.data.materiality_threshold_pct || 1.0,
              physical_acute: data.data.physical_risks?.acute || 0,
              physical_chronic: data.data.physical_risks?.chronic || 0,
              transition_policy: data.data.transition_risks?.policy || 0,
              transition_technology: data.data.transition_risks?.technology || 0,
              transition_market: data.data.transition_risks?.market || 0,
              transition_reputation: data.data.transition_risks?.reputation || 0,
            }));
          }
        }
      } catch (error) {
        console.error('Error loading ESG data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAllData();
  }, []);
  
  // Save functions
  const saveData = async (section, endpoint, data) => {
    setSaving(prev => ({ ...prev, [section]: true }));
    setSaveStatus(prev => ({ ...prev, [section]: null }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/esg-data/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        setSaveStatus(prev => ({ ...prev, [section]: 'success' }));
        setTimeout(() => setSaveStatus(prev => ({ ...prev, [section]: null })), 3000);
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error(`Error saving ${section}:`, error);
      setSaveStatus(prev => ({ ...prev, [section]: 'error' }));
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  };
  
  // File Upload Functions
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = async (files) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/pdf'
    ];
    
    const validFiles = files.filter(f => 
      validTypes.includes(f.type) || 
      f.name.endsWith('.csv') || 
      f.name.endsWith('.xlsx') || 
      f.name.endsWith('.xls') ||
      f.name.endsWith('.pdf')
    );
    
    if (validFiles.length === 0) {
      alert('Please upload CSV, Excel, or PDF files only.');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/api/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
        
        if (response.ok) {
          const result = await response.json();
          setUploadedFiles(prev => [...prev, {
            id: result.file_id || Date.now(),
            name: file.name,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toISOString(),
            status: 'success'
          }]);
        } else {
          throw new Error('Upload failed');
        }
        
        setUploadProgress(prev => prev + (100 / validFiles.length));
      } catch (error) {
        console.error('Upload error:', error);
        setUploadedFiles(prev => [...prev, {
          id: Date.now(),
          name: file.name,
          size: file.size,
          status: 'error'
        }]);
      }
    }
    
    setUploading(false);
    setUploadProgress(100);
    setTimeout(() => setUploadProgress(0), 2000);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // Calculate derived values
  const taxonomyEligiblePct = taxonomyData.total_revenue > 0 
    ? ((taxonomyData.eligible_revenue / taxonomyData.total_revenue) * 100).toFixed(1) 
    : 0;
  const taxonomyAlignedPct = taxonomyData.total_revenue > 0 
    ? ((taxonomyData.aligned_revenue / taxonomyData.total_revenue) * 100).toFixed(1) 
    : 0;
  const scope3Total = Object.values(scope3Data.categories).reduce((a, b) => a + b, 0);
  const totalEmissions = scope3Data.scope1 + scope3Data.scope2_location + scope3Total;
  const secTotalImpact = secData.physical_acute + secData.physical_chronic + 
    secData.transition_policy + secData.transition_technology + 
    secData.transition_market + secData.transition_reputation;
  const secMaterialityThreshold = secData.total_revenue * (secData.materiality_threshold_pct / 100);
  const secDisclosureRequired = secTotalImpact >= secMaterialityThreshold && secData.total_revenue > 0;
  
  // IRO material topics count
  const materialTopics = iroData.topics.filter(t => 
    (t.impact >= iroData.materiality_threshold || t.financial >= iroData.materiality_threshold)
  ).length;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="animate-spin text-emerald-600" size={32} />
        <span className="ml-3 text-slate-600">Loading your ESG data...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="esg-data-entry-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileText size={32} />
              <h1 className="text-3xl font-bold">Manual Entry</h1>
            </div>
            <p className="text-emerald-100">
              Enter your organization&apos;s ESG data to generate accurate compliance reports
            </p>
          </div>
          <div className="text-right bg-white/10 rounded-lg p-4">
            <p className="text-sm text-emerald-200">Data Completeness</p>
            <p className="text-3xl font-bold">
              {Math.round((
                (taxonomyData.total_revenue > 0 ? 25 : 0) +
                (scope3Total > 0 ? 25 : 0) +
                (materialTopics > 0 ? 25 : 0) +
                (secTotalImpact > 0 ? 25 : 0)
              ))}%
            </p>
          </div>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <Building2 size={18} />
            <span className="text-sm font-medium">EU Taxonomy</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{taxonomyAlignedPct}%</p>
          <p className="text-xs text-slate-500">Aligned Revenue</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <Leaf size={18} />
            <span className="text-sm font-medium">Total Emissions</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{(totalEmissions / 1000000).toFixed(1)}M</p>
          <p className="text-xs text-slate-500">tCO2e (All Scopes)</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-purple-600 mb-2">
            <Target size={18} />
            <span className="text-sm font-medium">IRO Assessment</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{materialTopics}/10</p>
          <p className="text-xs text-slate-500">Material Topics</p>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-amber-600 mb-2">
            <Scale size={18} />
            <span className="text-sm font-medium">SEC Climate</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">${(secTotalImpact / 1000000).toFixed(1)}M</p>
          <p className="text-xs text-slate-500">{secDisclosureRequired ? 'Disclosure Required' : 'Below Threshold'}</p>
        </div>
      </div>
      
      {/* File Upload Section */}
      <DataSection
        title="Upload Data Files"
        icon={Upload}
        color="bg-indigo-600"
        isOpen={activeSection === 'upload'}
        onToggle={() => setActiveSection(activeSection === 'upload' ? null : 'upload')}
        badge={uploadedFiles.length > 0 ? `${uploadedFiles.length} files` : null}
      >
        <div className="space-y-6">
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info size={16} className="text-indigo-600 mt-0.5" />
              <p className="text-sm text-indigo-800">
                Upload your ESG data files (CSV, Excel, PDF) for AI-powered analysis and automatic KPI extraction. 
                Files will be processed and data will be extracted to populate your ESG metrics.
              </p>
            </div>
          </div>
          
          {/* Drag & Drop Upload Area */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              dragActive 
                ? 'border-indigo-500 bg-indigo-50' 
                : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
            }`}
          >
            <input
              type="file"
              multiple
              accept=".csv,.xlsx,.xls,.pdf"
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              data-testid="file-upload-input"
            />
            <div className="flex flex-col items-center gap-3">
              <div className={`p-4 rounded-full ${dragActive ? 'bg-indigo-200' : 'bg-slate-100'}`}>
                <Upload size={32} className={dragActive ? 'text-indigo-600' : 'text-slate-400'} />
              </div>
              <div>
                <p className="font-semibold text-slate-900">
                  {dragActive ? 'Drop files here' : 'Drag & drop files here'}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  or click to browse • CSV, Excel, PDF supported
                </p>
              </div>
            </div>
            
            {/* Upload Progress */}
            {uploading && (
              <div className="mt-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Loader className="animate-spin text-indigo-600" size={16} />
                  <span className="text-sm text-indigo-600">Uploading...</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-slate-900">Uploaded Files</h4>
              {uploadedFiles.map((file) => (
                <div 
                  key={file.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    file.status === 'error' ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      file.name.endsWith('.pdf') ? 'bg-red-100' :
                      file.name.endsWith('.csv') ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      {file.name.endsWith('.pdf') ? (
                        <FileText size={18} className="text-red-600" />
                      ) : file.name.endsWith('.csv') ? (
                        <File size={18} className="text-green-600" />
                      ) : (
                        <FileSpreadsheet size={18} className="text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{file.name}</p>
                      <p className="text-xs text-slate-500">
                        {formatFileSize(file.size)}
                        {file.status === 'error' && (
                          <span className="text-red-600 ml-2">• Upload failed</span>
                        )}
                        {file.status === 'success' && (
                          <span className="text-green-600 ml-2">• Uploaded</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(file.id)}
                    className="p-1 hover:bg-slate-100 rounded"
                  >
                    <X size={16} className="text-slate-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Quick Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
            <button
              onClick={() => navigate('/data-upload')}
              className="flex-1 py-3 px-4 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center justify-center gap-2"
              data-testid="go-to-data-upload-btn"
            >
              <Upload size={18} />
              Advanced Upload & AI Analysis
              <ArrowRight size={16} />
            </button>
            <button
              onClick={() => navigate('/evidence-library')}
              className="flex-1 py-3 px-4 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 flex items-center justify-center gap-2"
              data-testid="go-to-evidence-library-btn"
            >
              <FileText size={18} />
              View Regulatory Documents
            </button>
          </div>
        </div>
      </DataSection>
      
      <PremiumGate featureKey="export-pdf">
        {/* EU Taxonomy Section */}
        <DataSection
          title="EU Taxonomy Assessment"
          icon={Building2}
          color="bg-blue-600"
          isOpen={activeSection === 'taxonomy'}
          onToggle={() => setActiveSection(activeSection === 'taxonomy' ? null : 'taxonomy')}
          badge={taxonomyData.total_revenue > 0 ? 'Entered' : null}
        >
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Enter your company&apos;s financial data to calculate EU Taxonomy alignment. 
                  This data feeds into the Export Center for consolidated reporting.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField
                label="Total Revenue"
                value={taxonomyData.total_revenue}
                onChange={(v) => setTaxonomyData(prev => ({ ...prev, total_revenue: v }))}
                placeholder="e.g., 100000000"
                suffix="€"
                help="Annual revenue in euros"
              />
              <InputField
                label="Eligible Revenue"
                value={taxonomyData.eligible_revenue}
                onChange={(v) => setTaxonomyData(prev => ({ ...prev, eligible_revenue: v }))}
                placeholder="e.g., 68000000"
                suffix="€"
                help="Revenue from taxonomy-eligible activities"
              />
              <InputField
                label="Aligned Revenue"
                value={taxonomyData.aligned_revenue}
                onChange={(v) => setTaxonomyData(prev => ({ ...prev, aligned_revenue: v }))}
                placeholder="e.g., 48000000"
                suffix="€"
                help="Revenue meeting taxonomy criteria"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField
                label="Total CapEx"
                value={taxonomyData.total_capex}
                onChange={(v) => setTaxonomyData(prev => ({ ...prev, total_capex: v }))}
                suffix="€"
              />
              <InputField
                label="Green CapEx"
                value={taxonomyData.green_capex}
                onChange={(v) => setTaxonomyData(prev => ({ ...prev, green_capex: v }))}
                suffix="€"
              />
              <div className="flex items-end">
                <div className="bg-emerald-100 rounded-lg p-3 w-full text-center">
                  <p className="text-sm text-emerald-700">Green CapEx %</p>
                  <p className="text-2xl font-bold text-emerald-800">
                    {taxonomyData.total_capex > 0 ? ((taxonomyData.green_capex / taxonomyData.total_capex) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField
                label="Total OpEx"
                value={taxonomyData.total_opex}
                onChange={(v) => setTaxonomyData(prev => ({ ...prev, total_opex: v }))}
                suffix="€"
              />
              <InputField
                label="Green OpEx"
                value={taxonomyData.green_opex}
                onChange={(v) => setTaxonomyData(prev => ({ ...prev, green_opex: v }))}
                suffix="€"
              />
              <div className="flex items-end">
                <div className="bg-emerald-100 rounded-lg p-3 w-full text-center">
                  <p className="text-sm text-emerald-700">Green OpEx %</p>
                  <p className="text-2xl font-bold text-emerald-800">
                    {taxonomyData.total_opex > 0 ? ((taxonomyData.green_opex / taxonomyData.total_opex) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                <p className="text-sm text-blue-100">Eligible Revenue</p>
                <p className="text-3xl font-bold">{taxonomyEligiblePct}%</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg p-4 text-white">
                <p className="text-sm text-emerald-100">Aligned Revenue</p>
                <p className="text-3xl font-bold">{taxonomyAlignedPct}%</p>
              </div>
            </div>
            
            <button
              onClick={() => saveData('taxonomy', 'eu-taxonomy', {
                ...taxonomyData,
                eligible_pct: parseFloat(taxonomyEligiblePct),
                aligned_pct: parseFloat(taxonomyAlignedPct),
              })}
              disabled={saving.taxonomy}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="save-taxonomy-btn"
            >
              {saving.taxonomy ? <Loader className="animate-spin" size={18} /> : <Save size={18} />}
              Save EU Taxonomy Data
              {saveStatus.taxonomy === 'success' && <CheckCircle size={18} className="text-emerald-300" />}
              {saveStatus.taxonomy === 'error' && <AlertTriangle size={18} className="text-red-300" />}
            </button>
          </div>
        </DataSection>
        
        {/* Scope 3 Emissions Section */}
        <DataSection
          title="GHG Emissions (Scope 1, 2 & 3)"
          icon={Leaf}
          color="bg-green-600"
          isOpen={activeSection === 'scope3'}
          onToggle={() => setActiveSection(activeSection === 'scope3' ? null : 'scope3')}
          badge={scope3Total > 0 ? 'Entered' : null}
        >
          <div className="space-y-6">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-green-600 mt-0.5" />
                <p className="text-sm text-green-800">
                  Enter emissions data in tonnes CO2 equivalent (tCO2e). Scope 3 covers all 15 GHG Protocol categories.
                </p>
              </div>
            </div>
            
            {/* Scope 1 & 2 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField
                label="Scope 1 (Direct Emissions)"
                value={scope3Data.scope1}
                onChange={(v) => setScope3Data(prev => ({ ...prev, scope1: v }))}
                suffix="tCO2e"
                help="On-site fuel combustion, company vehicles"
              />
              <InputField
                label="Scope 2 (Location-based)"
                value={scope3Data.scope2_location}
                onChange={(v) => setScope3Data(prev => ({ ...prev, scope2_location: v }))}
                suffix="tCO2e"
                help="Grid electricity using average emission factors"
              />
              <InputField
                label="Scope 2 (Market-based)"
                value={scope3Data.scope2_market}
                onChange={(v) => setScope3Data(prev => ({ ...prev, scope2_market: v }))}
                suffix="tCO2e"
                help="Electricity using supplier-specific factors"
              />
            </div>
            
            {/* Scope 3 Categories */}
            <h4 className="font-semibold text-slate-900 mt-6">Scope 3 Categories</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {SCOPE3_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <div key={cat.id} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={16} className="text-green-600" />
                      <span className="text-sm font-medium text-slate-700 truncate">{cat.name}</span>
                    </div>
                    <input
                      type="number"
                      value={scope3Data.categories[cat.id] || ''}
                      onChange={(e) => setScope3Data(prev => ({
                        ...prev,
                        categories: { ...prev.categories, [cat.id]: parseFloat(e.target.value) || 0 }
                      }))}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                    <p className="text-xs text-slate-400 mt-1">tCO2e</p>
                  </div>
                );
              })}
            </div>
            
            {/* Totals Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="bg-blue-100 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-600">Scope 1</p>
                <p className="text-xl font-bold text-blue-800">{(scope3Data.scope1 / 1000).toFixed(0)}k</p>
              </div>
              <div className="bg-purple-100 rounded-lg p-3 text-center">
                <p className="text-xs text-purple-600">Scope 2</p>
                <p className="text-xl font-bold text-purple-800">{(scope3Data.scope2_location / 1000).toFixed(0)}k</p>
              </div>
              <div className="bg-green-100 rounded-lg p-3 text-center">
                <p className="text-xs text-green-600">Scope 3</p>
                <p className="text-xl font-bold text-green-800">{(scope3Total / 1000000).toFixed(1)}M</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3 text-center text-white">
                <p className="text-xs text-slate-300">Total</p>
                <p className="text-xl font-bold">{(totalEmissions / 1000000).toFixed(1)}M</p>
              </div>
            </div>
            
            <button
              onClick={() => saveData('scope3', 'scope3-emissions', {
                ...scope3Data,
                total_scope3: scope3Total,
              })}
              disabled={saving.scope3}
              className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="save-scope3-btn"
            >
              {saving.scope3 ? <Loader className="animate-spin" size={18} /> : <Save size={18} />}
              Save Emissions Data
              {saveStatus.scope3 === 'success' && <CheckCircle size={18} className="text-emerald-300" />}
            </button>
          </div>
        </DataSection>
        
        {/* IRO Assessment Section */}
        <DataSection
          title="IRO Assessment (Double Materiality)"
          icon={Target}
          color="bg-purple-600"
          isOpen={activeSection === 'iro'}
          onToggle={() => setActiveSection(activeSection === 'iro' ? null : 'iro')}
          badge={materialTopics > 0 ? `${materialTopics} Material` : null}
        >
          <div className="space-y-6">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-purple-600 mt-0.5" />
                <p className="text-sm text-purple-800">
                  Score each ESRS topic from 1-5 for both Impact (effect on people/environment) and Financial (effect on company value). 
                  Topics scoring ≥{iroData.materiality_threshold} on either dimension are considered material.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-slate-700">Materiality Threshold:</label>
              <select
                value={iroData.materiality_threshold}
                onChange={(e) => setIroData(prev => ({ ...prev, materiality_threshold: parseFloat(e.target.value) }))}
                className="px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value={2.5}>2.5 (Low)</option>
                <option value={3.0}>3.0 (Standard)</option>
                <option value={3.5}>3.5 (High)</option>
                <option value={4.0}>4.0 (Very High)</option>
              </select>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left">ESRS Topic</th>
                    <th className="px-4 py-3 text-center">Impact Score (1-5)</th>
                    <th className="px-4 py-3 text-center">Financial Score (1-5)</th>
                    <th className="px-4 py-3 text-center">Material?</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {iroData.topics.map((topic, idx) => {
                    const isMaterial = topic.impact >= iroData.materiality_threshold || topic.financial >= iroData.materiality_threshold;
                    return (
                      <tr key={topic.code} className={isMaterial ? 'bg-purple-50' : ''}>
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-900">{topic.code}</span>
                          <span className="text-slate-500 ml-2">{topic.name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            max="5"
                            step="0.5"
                            value={topic.impact || ''}
                            onChange={(e) => {
                              const newTopics = [...iroData.topics];
                              newTopics[idx].impact = parseFloat(e.target.value) || 0;
                              setIroData(prev => ({ ...prev, topics: newTopics }));
                            }}
                            className="w-20 px-2 py-1 border border-slate-300 rounded text-center"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="1"
                            max="5"
                            step="0.5"
                            value={topic.financial || ''}
                            onChange={(e) => {
                              const newTopics = [...iroData.topics];
                              newTopics[idx].financial = parseFloat(e.target.value) || 0;
                              setIroData(prev => ({ ...prev, topics: newTopics }));
                            }}
                            className="w-20 px-2 py-1 border border-slate-300 rounded text-center"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isMaterial ? (
                            <span className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded">MATERIAL</span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-200 text-slate-500 text-xs rounded">Not Material</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <div className="flex justify-between items-center mt-4 p-4 bg-purple-100 rounded-lg">
              <div>
                <p className="text-sm text-purple-700">Material Topics</p>
                <p className="text-2xl font-bold text-purple-900">{materialTopics} of 10</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-purple-700">Threshold</p>
                <p className="text-2xl font-bold text-purple-900">≥ {iroData.materiality_threshold}</p>
              </div>
            </div>
            
            <button
              onClick={() => saveData('iro', 'iro-assessment', iroData)}
              disabled={saving.iro}
              className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="save-iro-btn"
            >
              {saving.iro ? <Loader className="animate-spin" size={18} /> : <Save size={18} />}
              Save IRO Assessment
              {saveStatus.iro === 'success' && <CheckCircle size={18} className="text-emerald-300" />}
            </button>
          </div>
        </DataSection>
        
        {/* SEC Climate Section */}
        <DataSection
          title="SEC Climate Disclosure"
          icon={Scale}
          color="bg-amber-600"
          isOpen={activeSection === 'sec'}
          onToggle={() => setActiveSection(activeSection === 'sec' ? null : 'sec')}
          badge={secDisclosureRequired ? 'Disclosure Required' : null}
        >
          <div className="space-y-6">
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Enter financial impacts from climate-related events. Disclosure is required if total impact exceeds 
                  the materiality threshold (typically 1% of revenue, expenses, or assets).
                </p>
              </div>
            </div>
            
            {/* Financial Context */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField
                label="Total Revenue (Fiscal Year)"
                value={secData.total_revenue}
                onChange={(v) => setSecData(prev => ({ ...prev, total_revenue: v }))}
                suffix="$"
                help="Annual revenue for threshold calculation"
              />
              <InputField
                label="Total Assets"
                value={secData.total_assets}
                onChange={(v) => setSecData(prev => ({ ...prev, total_assets: v }))}
                suffix="$"
              />
              <InputField
                label="Materiality Threshold"
                value={secData.materiality_threshold_pct}
                onChange={(v) => setSecData(prev => ({ ...prev, materiality_threshold_pct: v }))}
                suffix="%"
                help="Usually 1% of revenue"
              />
            </div>
            
            {/* Physical Risks */}
            <h4 className="font-semibold text-slate-900">Physical Risks ($ Impact)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Acute Physical Risks"
                value={secData.physical_acute}
                onChange={(v) => setSecData(prev => ({ ...prev, physical_acute: v }))}
                suffix="$"
                help="Storms, floods, wildfires, extreme weather"
              />
              <InputField
                label="Chronic Physical Risks"
                value={secData.physical_chronic}
                onChange={(v) => setSecData(prev => ({ ...prev, physical_chronic: v }))}
                suffix="$"
                help="Sea level rise, temperature changes, water stress"
              />
            </div>
            
            {/* Transition Risks */}
            <h4 className="font-semibold text-slate-900">Transition Risks ($ Impact)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Policy & Legal Risks"
                value={secData.transition_policy}
                onChange={(v) => setSecData(prev => ({ ...prev, transition_policy: v }))}
                suffix="$"
                help="Carbon pricing, regulations, litigation"
              />
              <InputField
                label="Technology Risks"
                value={secData.transition_technology}
                onChange={(v) => setSecData(prev => ({ ...prev, transition_technology: v }))}
                suffix="$"
                help="Asset stranding, technology shifts"
              />
              <InputField
                label="Market Risks"
                value={secData.transition_market}
                onChange={(v) => setSecData(prev => ({ ...prev, transition_market: v }))}
                suffix="$"
                help="Consumer behavior, demand shifts"
              />
              <InputField
                label="Reputation Risks"
                value={secData.transition_reputation}
                onChange={(v) => setSecData(prev => ({ ...prev, transition_reputation: v }))}
                suffix="$"
                help="Stakeholder perception, ESG ratings"
              />
            </div>
            
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-slate-100 rounded-lg p-4">
                <p className="text-sm text-slate-600">Total Climate Impact</p>
                <p className="text-2xl font-bold text-slate-900">${(secTotalImpact / 1000000).toFixed(2)}M</p>
              </div>
              <div className="bg-slate-100 rounded-lg p-4">
                <p className="text-sm text-slate-600">Materiality Threshold</p>
                <p className="text-2xl font-bold text-slate-900">${(secMaterialityThreshold / 1000000).toFixed(2)}M</p>
              </div>
              <div className={`rounded-lg p-4 ${secDisclosureRequired ? 'bg-red-100' : 'bg-green-100'}`}>
                <p className={`text-sm ${secDisclosureRequired ? 'text-red-600' : 'text-green-600'}`}>Disclosure Status</p>
                <p className={`text-2xl font-bold ${secDisclosureRequired ? 'text-red-700' : 'text-green-700'}`}>
                  {secDisclosureRequired ? 'REQUIRED' : 'Not Required'}
                </p>
              </div>
            </div>
            
            <button
              onClick={() => saveData('sec', 'sec-climate', {
                ...secData,
                total_climate_impact: secTotalImpact,
                disclosure_required: secDisclosureRequired,
              })}
              disabled={saving.sec}
              className="w-full py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="save-sec-btn"
            >
              {saving.sec ? <Loader className="animate-spin" size={18} /> : <Save size={18} />}
              Save SEC Climate Data
              {saveStatus.sec === 'success' && <CheckCircle size={18} className="text-emerald-300" />}
            </button>
          </div>
        </DataSection>
      </PremiumGate>
      
      {/* Export Reminder */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg mb-1">Ready to Generate Reports?</h3>
            <p className="text-slate-300 text-sm">
              Your entered data will be used in the Export Center for consolidated ESG reporting.
            </p>
          </div>
          <a
            href="/reports/export"
            className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2"
          >
            Go to Export Center
            <ChevronRight size={18} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ESGDataEntry;
